import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AddNewDataPage } from "./modules/add-new-data/add-new-data-page";
import { WebAdapterProvider, WorkspaceRootContext, type WebWorkspaceProps } from "./web-adapter";
import type { AddNewDataFormValues } from "./modules/add-new-data/lib/schema";
import styles from "./desktop.css?inline";

/** Keep the complete Desktop design layer, including preflight, out of the portal shell. */
export function DesktopWorkspace(props: WebWorkspaceProps) {
  const host = useRef<HTMLDivElement>(null);
  const [root, setRoot] = useState<ShadowRoot | null>(null);
  const [seed, setSeed] = useState<AddNewDataFormValues>();

  useLayoutEffect(() => {
    if (host.current) setRoot(host.current.shadowRoot ?? host.current.attachShadow({ mode: "open" }));
  }, []);

  useEffect(() => {
    if (!root) return;

    const commitFocusedFieldBeforeWizardAction = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest("button");
      if (!button) return;

      const actionLabel = (button.textContent ?? "").replace(/\s+/g, " ").trim();
      if (!isValueCommittingWizardAction(actionLabel)) return;

      const activeElement = root.activeElement;
      if (!isEditableFormControl(activeElement)) return;

      // The Desktop questionnaire is rendered inside an open ShadowRoot. The
      // shared Desktop submit handler reads document.activeElement, which is the
      // Shadow host rather than the focused input. Commit the actual focused
      // Web field before Next/Submit runs so React Hook Form receives the latest
      // calendar, time, select, or text value instead of validating stale state.
      activeElement.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
      activeElement.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
      activeElement.blur();
    };

    root.addEventListener("pointerdown", commitFocusedFieldBeforeWizardAction, true);
    return () => root.removeEventListener("pointerdown", commitFocusedFieldBeforeWizardAction, true);
  }, [root]);

  return <div ref={host} data-desktop-workspace="true">{root && createPortal(
    <WorkspaceRootContext.Provider value={root}>
      <style>{styles}</style>
      <WebAdapterProvider {...props}
        key={`${props.draftOwnerId}:${props.context.municipality.id}:${props.barangayId}`}
        seed={seed}
        onSelection={(id, values) => {
          if (id && !props.barangays.some((item) => item.id === id)) return;
          setSeed(!props.barangayId ? values : undefined);
          props.onBarangayChange(id);
        }}>
        <AddNewDataPage />
      </WebAdapterProvider>
    </WorkspaceRootContext.Provider>, root,
  )}</div>;
}

function isEditableFormControl(element: Element | null): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
  return element instanceof HTMLInputElement
    || element instanceof HTMLSelectElement
    || element instanceof HTMLTextAreaElement;
}

function isValueCommittingWizardAction(label: string) {
  return label === "Next"
    || label === "Submit"
    || label === "Retry Submission"
    || label === "Save Changes"
    || label === "Retry Save Changes";
}
